"use client";
import { setAiGlobalSearch } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { HeroUniversities } from "../common/Helper";
import HeroInputSearch from "../common/HeroInputSearch";

import { urlFor } from "@/sanity/lib/image";
import { GlobalSearchCard } from "@/types/homepage";
import Image from "next/image";
import Link from "next/link";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

type Country = {
  country_id: number;
  country_name: string;
  priority_ranking: number;
  country_currency: string;
  university_count: string;
};
const STORAGE_KEY = "selected_country";

const SearchSmart = ({
  content,
}: {
  content: GlobalSearchCard[] | undefined;
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { countryList } = useAppSelector((state) => state.country);
  // const [searchFocused, setSearchFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [onSearch, setOnSearch] = useState(false);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Country[]>([]);
  const [mobileInputValue, setMobileInputValue] = useState("");
  const [swiperRef, setSwiperRef] = useState<any>(null);
  const ref = useRef<HTMLDivElement>(null);

  const handleMobileSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mobileInputValue.trim()) {
      dispatch(setAiGlobalSearch(mobileInputValue.trim()));
      router.push("/ai-course-finder");
    }
  };

  useEffect(() => {
    if (!countryList?.length) return;

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        // ✅ FIX: ensure always array
        if (Array.isArray(parsed)) {
          setSelected(parsed);
        } else if (parsed) {
          setSelected([parsed]); // convert old single value → array
        } else {
          setSelected([]);
        }
      } catch {
        setSelected([]);
      }
    } else {
      setSelected([]);
    }
  }, [countryList]);

  const handleSelect = (country: Country) => {
    let updated: Country[] = [];

    const exists = selected.some((c) => c.country_id === country.country_id);

    if (exists) {
      updated = selected.filter((c) => c.country_id !== country.country_id);
    } else {
      updated = [...selected, country];
    }

    setSelected(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const formatName = (name: string) => {
    const normalized = name.includes(" - ")
      ? name.split(" - ").pop() || name
      : name;

    return normalized
      .replace(/[_-]/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (match) => match.toUpperCase())
      .replace(/\bUk\b/g, "UK")
      .replace(/\bUsa\b/g, "USA")
      .replace(/\bUae\b/g, "UAE");
  };

  const handlePrev = () => {
    setDirection(-1);
    setActiveIndex(
      (prev) => (prev - 1 + HeroUniversities.length) % HeroUniversities.length,
    );
  };

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % HeroUniversities.length);
  }, [HeroUniversities.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 3000);

    return () => clearInterval(interval);
  }, [handleNext]);

  // useEffect(() => {
  //   if (isPaused) return;

  //   const interval = setInterval(() => {
  //     handleNext();
  //   }, 3000);

  //   return () => clearInterval(interval);
  // }, [handleNext, isPaused]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTagClick = (tag: string) => {
    dispatch(setAiGlobalSearch(tag));
    router.push("/ai-course-finder");
    document.body.classList.remove("!overflow-hidden");
  };

  return (
    <div className="container max-w-[1400px] mx-auto mt-[32px] sm:-mt-[20px] md:-mt-[80px] mb-[40px] sm:mb-[60px] md:mb-[80px] px-4 xxs:px-6 sm:px-[40px] overflow-hidden">
      <div className="bg-[#021A38] rounded-2xl shadow-[0px_-4px-6px_0px_#0000001F] py-6 px-4 xxs:px-5 sm:py-[32px] sm:px-[40px]">
        <h2 className="font-semibold text-white text-[14px] xxs:text-[16px] xs:text-[18px] sm:text-2xl ff_poppins text-start">
          Search Smarter. Study Better.
        </h2>

        {/* Mobile Search Bar (Only below sm) */}
        <div className="block sm:hidden mt-2">
          <form onSubmit={handleMobileSubmit} className="flex items-center gap-3 bg-white rounded-[18px] px-[14px] pb-[14px] pt-[10px] shadow-[0px_4px_12px_rgba(0,0,0,0.05)] border border-gray-200">
            <Search size={14} className="text-[#374151] shrink-0" />
            <input
              type="text"
              value={mobileInputValue}
              onChange={(e) => setMobileInputValue(e.target.value)}
              placeholder='Try "Msc Data science" or "MBA in UK"'
              aria-label="Search courses or universities"
              className="w-full text-[11px] xxs:text-xs focus:outline-hidden text-[#111827] placeholder:text-[#4B5563] font-semibold"
            />
          </form>
        </div>

        {/* Desktop Search Bar (Only sm and above) */}
        <div className="hidden sm:block mt-6 bg-white rounded-[24px] px-[11px] sm:px-5 py-[11px] sm:py-4">
          <motion.div className="relative" transition={{ duration: 0.2 }}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-3 flex-1">
                {/* Country Dropdown */}
                <div ref={ref} className="relative inline-block">
                  {/* Trigger */}
                  <div
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    <Globe2
                      size={16}
                      className="sm:w-[18px] sm:h-[18px]"
                      style={{ color: "#6B7A90" }}
                    />

                    <div className="flex flex-wrap items-center gap-1 max-w-[180px] ff_poppins capitalize">
                      {selected.length > 0 ? (
                        selected.slice(0, 2).map((c) => (
                          <span
                            key={c.country_id}
                            className="text-[10px] sm:text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-600"
                          >
                            {formatName(c.country_name)}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs sm:text-sm">
                          All{" "}
                          <span className="hidden sm:inline">Countries</span>
                        </span>
                      )}

                      {selected.length > 2 && (
                        <span className="text-[10px] text-gray-500">
                          +{selected.length - 2}
                        </span>
                      )}
                    </div>

                    <motion.div
                      animate={{ rotate: open ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ChevronDown
                        size={14}
                        className="sm:w-4 sm:h-4"
                        style={{ color: "#6B7A90" }}
                      />
                    </motion.div>
                  </div>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto h-[200px] z-50 "
                      >
                        {Array.isArray(countryList) && countryList.map((country) => {
                          const isSelected =
                            Array.isArray(selected) &&
                            selected.some(
                              (c) => c.country_id === country.country_id,
                            );

                          return (
                            <div
                              key={country.country_id}
                              onClick={() => handleSelect(country)}
                              className={`px-4 py-2 text-sm cursor-pointer transition-colors flex justify-between ff_poppins capitalize ${isSelected
                                ? "bg-blue-50 text-blue-600"
                                : "hover:bg-gray-100 text-gray-700"
                                }`}
                            >
                              {formatName(country.country_name)}

                              {isSelected && <span>✓</span>}
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <HeroInputSearch isHeroSection={true} onSearch={onSearch} />
              </div>

              {/* Search Button */}
              <button
                onClick={() => setOnSearch(!onSearch)}
                className="px-4 sm:px-5 py-2.5 rounded-full text-white font-semibold text-sm sm:text-base flex items-center justify-center gap-2 shadow-lg bg-primary-gradient cursor-pointer"
              >
                <Search size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Search</span>
              </button>
            </div>
          </motion.div>
        </div>

        <div className="pt-3 pb-0 sm:py-[32px] flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
          <p className="flex items-center gap-1 ff_poppins font-medium text-xs xxs:text-sm sm:text-base text-white shrink-0">
            <span className="inline-block -mt-2 shrink-0">
              <Image
                className="max-w-[30px] w-full sm:max-w-[40px]"
                width={40}
                height={40}
                src="/assets/gif/fire.gif"
                alt="Fire"
                unoptimized
              />
            </span>
            Trending:
          </p>

          <div className="flex-1 min-w-0">
            <Swiper
              slidesPerView="auto"
              spaceBetween={8}
              loop={true}
              speed={3000}
              autoplay={{
                delay: 0,
                disableOnInteraction: false,
              }}
              modules={[Autoplay]}
              className="w-full marquee-swiper"
            >
              {[
                "Study In Germany",
                "MS in AI",
                "MBA in Canada",
                "Data Science",
              ].map((item, index) => (
                <SwiperSlide key={index} className="!w-auto pb-3 sm:pb-0">
                  <span
                    onClick={() => handleTagClick(item)}
                    className="bg-white whitespace-nowrap inline-block ff_poppins rounded-full py-1.5 px-3 text-[#0B57D0] font-semibold text-xs xxs:text-sm cursor-pointer shadow-xs hover:bg-gray-50"
                  >
                    {item}
                  </span>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>

        {/* SLIDER */}
        <>
          <Swiper
            onSwiper={setSwiperRef}
            slidesPerView={1}
            spaceBetween={20}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              768: {
                slidesPerView: 2,
              },
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            pagination={{ el: ".swiper-pagination-custom", clickable: true }}
            modules={[Pagination, Autoplay]}
            className="homepageSwiper"
          >
            {content &&
              content.map((chunk, slideIndex) => {
                const mainImage = chunk.image
                  ? urlFor(chunk.image).width(550).height(310).url()
                  : null;
                return (
                  <SwiperSlide key={slideIndex} className="h-full">
                    <Link
                      href={`/${chunk.universitySlug}`}
                      className="bg-white rounded-[16px] overflow-hidden shadow-[0px_4px_6px_-4px_#0000001A,0px_10px_15px_-3px_#0000001A] flex flex-col lg:flex-row lg:gap-5 h-full"
                    >
                      <div className="min-h-full">
                        <Image
                          className="w-full h-full object-cover object-center"
                          width={290}
                          height={210}
                          src={mainImage || ""}
                          alt="image"
                        />
                      </div>
                      <div className="py-5 px-4 lg:pl-0 h-full w-full pr-5">
                        <p className="text-[#101828] ff_poppins font-semibold text-lg">
                          {chunk.title}
                        </p>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {chunk.labels.map((item, index) => (
                            <div
                              key={index}
                              className="border border-[#CBD5E1] rounded-full bg-[#F1F5F9] py-[3px] px-[10px] ff_poppins text-xs text-[#334155] font-medium"
                            >
                              {item}
                            </div>
                          ))}
                        </div>

                        <div className="my-[30px]">
                          <p className="bg-gradient-to-r from-[#1776F0] to-[#8F3EBC] bg-clip-text text-transparent text-sm font-medium ff_poppins">
                            QS Ranking — #{chunk.ranking}
                          </p>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-t-[#E6ECF2]">
                          <div className="flex items-center gap-2">
                            <span className="size-[10px] bg-[#2F7D5B] rounded-full"></span>
                            <p className="underline text-[#2F7D5B] text-[13px] ff_poppins font-semibold">
                              Check Eligibility
                            </p>
                          </div>
                          <div className="">
                            🎓{" "}
                            <p className="text-sm ff_poppins font-bold inline-block bg-gradient-to-r from-[#9810FA] to-[#155DFC] bg-clip-text text-transparent">
                              {chunk.tuitionFee}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </SwiperSlide>
                );
              })}
          </Swiper>

          {/* Custom Navigation & Pagination Control Bar */}
          <div className="flex items-center justify-center gap-4 mt-3">
            <button
              onClick={() => swiperRef?.slidePrev()}
              aria-label="Previous slide"
              className="w-9 h-9 rounded-full bg-[#E5EEF9] hover:bg-[#d0e1f7] flex items-center justify-center text-[#021A38] transition-all duration-200 cursor-pointer shrink-0 shadow-sm"
            >
              <ChevronLeft size={16} strokeWidth={2.5} />
            </button>

            <div className="swiper-pagination-custom homepageSwiper flex items-center gap-[0.5] sm:gap-1 [&_.swiper-pagination-bullet]:mx-0.5 relative [&_.swiper-pagination-bullet]:static !w-auto"></div>

            <button
              onClick={() => swiperRef?.slideNext()}
              aria-label="Next slide"
              className="w-9 h-9 rounded-full bg-[#E5EEF9] hover:bg-[#d0e1f7] flex items-center justify-center text-[#021A38] transition-all duration-200 cursor-pointer shrink-0 shadow-sm"
            >
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        </>
      </div>
    </div >
  );
};
export default SearchSmart;
